const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});
const relativeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

export const formatDate = (iso: string | null): string =>
  iso ? dateFormatter.format(new Date(iso)) : "—";

export const formatDateTime = (iso: string | null): string =>
  iso ? dateTimeFormatter.format(new Date(iso)) : "—";

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 31536000],
  ["month", 2592000],
  ["week", 604800],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

export function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const deltaSeconds = (Date.parse(iso) - Date.now()) / 1000;
  const abs = Math.abs(deltaSeconds);
  if (abs < 45) return "just now";
  for (const [unit, seconds] of UNITS) {
    if (abs >= seconds) return relativeFormatter.format(Math.round(deltaSeconds / seconds), unit);
  }
  return relativeFormatter.format(Math.round(deltaSeconds / 60), "minute");
}

/** Two-letter monogram for the avatar fallback. */
export function initials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/**
 * Milliseconds as a human span: "12 days, 4 hours" for a library, "3:47"
 * for a single track. Listening totals run to weeks, so the long form
 * stops at two units rather than spelling out every one.
 */
export function formatDuration(ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const plural = (n: number, unit: string) => `${n.toLocaleString()} ${unit}${n === 1 ? "" : "s"}`;

  if (days > 0) return hours ? `${plural(days, "day")}, ${plural(hours, "hour")}` : plural(days, "day");
  if (hours > 0) return minutes ? `${plural(hours, "hour")}, ${plural(minutes, "min")}` : plural(hours, "hour");
  return plural(Math.max(1, totalMinutes), "min");
}

/**
 * Compact span for a stat cell: "24d 14h", "6h 20m".
 *
 * The spelled-out form wraps to several lines in a narrow cell and drags
 * the height of everything beside it; this always fits on one.
 */
export function formatDurationShort(ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return hours ? `${days.toLocaleString()}d ${hours}h` : `${days.toLocaleString()}d`;
  if (hours > 0) return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${Math.max(1, totalMinutes)}m`;
}

/** Clock form, for a track length or playback position. */
export function formatClock(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export const formatCount = (n: number): string => n.toLocaleString();
